import { useEffect, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { X, AlertTriangle, Loader2, CheckCircle2, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { fmtDateTime } from '../i18n/format'

// Reset de VMC pelo serviço de reset. Com ele ligado no backend (ANCALAGON_URL), o
// "remover equipamento" pede um motivo, o backend assina e envia o pedido, e o resultado
// chega depois num callback, que é quem limpa os registros do painel. Aqui ficam as peças
// que o card verde (PendingDeviceCard) e o modal de ações do equipamento compartilham, e o
// acompanhamento, que vive fora dos dois: quando o callback apaga o equipamento, o card e
// o drawer somem da tela, e o resultado tem de continuar visível.

const POLL_MS = 3000
const POLL_MAX = 40 // 2 min
const FINAL = new Set(['completed', 'completed_with_warnings', 'failed'])
const KNOWN_ERRORS = new Set([
  'owner_mismatch', 'owner_unverified', 'owner_check_unavailable', 'vmc_busy', 'rate_limited',
  'role_not_allowed', 'resets_disabled', 'missing_reason', 'invalid_reason', 'invalid_ticket',
  'requester_unknown', 'ancalagon_unreachable', 'signer_unavailable', 'caller_token_unavailable',
])
const KNOWN_ABORTS = new Set(['reset_not_confirmed', 'pre_reset_failed', 'owner_changed', 'owner_unverifiable'])

// Uma ida ao /api/config por carga da página: o modo só muda num redeploy do backend.
let modePromise = null
export function getResetMode() {
  if (!modePromise) {
    modePromise = fetch('/api/config')
      .then(r => (r.ok ? r.json() : null))
      .then(cfg => !!cfg?.ancalagonReset)
      .catch(() => { modePromise = null; return false })
  }
  return modePromise
}

// null enquanto carrega; true quando o remover equipamento passa pelo serviço de reset.
export function useResetMode() {
  const [mode, setMode] = useState(null)
  useEffect(() => {
    let alive = true
    getResetMode().then(v => { if (alive) setMode(v) })
    return () => { alive = false }
  }, [])
  return mode
}

// O backend confere em runas (utf8.RuneCountInString); o spread conta do mesmo jeito.
export const reasonOk = (reason) => [...reason.trim()].length >= 3

export async function requestVmcReset(name, { reason, ticket }) {
  const res = await fetch(`/api/devices/${encodeURIComponent(name)}?device=true`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, ticket: ticket || undefined }),
  })
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const err = new Error(body?.error || `HTTP ${res.status}`)
    err.code = body?.code
    err.activeResetId = body?.active_reset_id
    err.resetId = body?.reset_id
    throw err
  }
  return body
}

// O erro chega com o código estável do serviço; a mensagem dele é para log, não para a
// tela. Código que a interface não conhece é falha de integração, não do operador.
export function resetErrorText(t, err) {
  const code = err?.code
  if (code && KNOWN_ERRORS.has(code)) return t(`ops:devices.reset.errors.${code}`, { id: err.activeResetId || '' })
  if (code && code !== 'internal' && code !== 'invalid_body') return t('ops:devices.reset.errors.integration', { code })
  return t('ops:devices.reset.errors.generic', { message: err?.message || '' })
}

// startVmcReset envia o pedido e entrega o acompanhamento ao VmcResetTracker. Sem resposta
// do serviço o pedido também é acompanhado: se chegou lá, o callback ainda o fecha.
// Devolve o texto do erro para o formulário, ou null.
export async function startVmcReset(t, name, { reason, ticket }) {
  try {
    const body = await requestVmcReset(name, { reason: reason.trim(), ticket: ticket.trim() })
    trackReset(name, body?.reset, body?.steps)
    return null
  } catch (err) {
    if (err.code === 'ancalagon_unreachable' && err.resetId) {
      trackReset(name, { reset_id: err.resetId, device_name: name, status: 'unknown', warnings: [] })
      return null
    }
    return resetErrorText(t, err)
  }
}

const inputCls = 'w-full px-2.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-volis-red/30 disabled:opacity-60'

export function ResetRequestFields({ reason, ticket, onReason, onTicket, disabled }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2.5">
      <label className="block">
        <span className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">{t('ops:devices.reset.reason')}</span>
        <textarea
          value={reason}
          onChange={e => onReason(e.target.value)}
          rows={2}
          maxLength={1000}
          disabled={disabled}
          placeholder={t('ops:devices.reset.reason_placeholder')}
          className={`${inputCls} py-1.5 resize-none`}
        />
        <span className="block text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{t('ops:devices.reset.reason_hint')}</span>
      </label>
      <label className="block">
        <span className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">{t('ops:devices.reset.ticket')}</span>
        <input
          type="text"
          value={ticket}
          onChange={e => onTicket(e.target.value)}
          maxLength={200}
          disabled={disabled}
          placeholder={t('ops:devices.reset.ticket_placeholder')}
          className={`${inputCls} h-9`}
        />
      </label>
    </div>
  )
}

// Formulário do card verde: confirmação pelo vmc-id digitado, como o delete de antes,
// mais o motivo que vai assinado no pedido.
export default function VmcResetDialog({ name, onClose }) {
  const { t } = useTranslation()
  const [typed, setTyped] = useState('')
  const [reason, setReason] = useState('')
  const [ticket, setTicket] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const blocked = typed.trim() !== name || !reasonOk(reason)

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  const submit = async () => {
    if (blocked || busy) return
    setBusy(true)
    setError(null)
    const msg = await startVmcReset(t, name, { reason, ticket })
    if (msg) {
      setError(msg)
      setBusy(false)
      return
    }
    onClose()
  }

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={18} className="text-volis-red mt-0.5 shrink-0" />
            <div>
              <h2 className="font-semibold text-slate-800 dark:text-slate-100">{t('ops:devices.reset.title')}</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">{name}</p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 disabled:opacity-50">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">{t('ops:devices.reset.intro')}</p>
          <ResetRequestFields reason={reason} ticket={ticket} onReason={setReason} onTicket={setTicket} disabled={busy} />
          <div className="space-y-1.5">
            <label className="block text-xs text-slate-500 dark:text-slate-400">
              {t('debug:confirm.type_to_enable', { word: name })}
            </label>
            <input
              type="text"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder={name}
              spellCheck={false}
              autoComplete="off"
              disabled={busy}
              className={`${inputCls} h-9 font-mono`}
            />
          </div>

          {error && <p className="text-xs text-volis-red">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              autoFocus
              disabled={busy}
              className="text-sm font-medium px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={submit}
              disabled={busy || blocked}
              className="text-sm font-semibold px-3 py-1.5 rounded-md text-white bg-volis-red hover:brightness-95 disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500"
            >
              {busy ? t('ops:devices.reset.sending') : t('ops:devices.reset.submit')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ---- Acompanhamento ------------------------------------------------------------------

let tracked = null
const listeners = new Set()
const notify = () => listeners.forEach(fn => fn())
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn) }
const snapshot = () => tracked

export function trackReset(name, reset, steps) {
  if (!reset?.reset_id) return
  const known = [steps, reset.steps].find(x => Array.isArray(x) && x.length) || []
  tracked = { name, reset, steps: known, key: reset.reset_id }
  notify()
}

// Montado uma vez na árvore autenticada (App.jsx).
export function VmcResetTracker() {
  const cur = useSyncExternalStore(subscribe, snapshot)
  if (!cur) return null
  return <ResetProgressDialog key={cur.key} name={cur.name} initial={cur.reset} initialSteps={cur.steps}
    onClose={() => { tracked = null; notify() }} />
}

// Para a interface o reset só termina com o callback aplicado (é ele que limpa o painel)
// ou com o pedido recusado, que nunca terá callback.
const isDone = (r) => !!r && (!!r.callback_at || r.status === 'rejected')

const errorCode = (e) => (typeof e === 'string' && e.includes(':') ? e.slice(0, e.indexOf(':')).trim() : '')

function statusTone(status) {
  switch (status) {
    case 'completed': return { cls: 'bg-status-op/15 text-status-op', Icon: CheckCircle2 }
    case 'completed_with_warnings': return { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300', Icon: AlertTriangle }
    case 'failed':
    case 'rejected': return { cls: 'bg-status-down/15 text-status-down', Icon: XCircle }
    default: return { cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', Icon: Loader2 }
  }
}

function stepDot(status) {
  switch (status) {
    case 'ok': return 'bg-status-op'
    case 'failed': return 'bg-status-down'
    case 'skipped': return 'bg-amber-400'
    default: return 'bg-slate-300 dark:bg-slate-600'
  }
}

function ResetProgressDialog({ name, initial, initialSteps, onClose }) {
  const { t } = useTranslation()
  const [reset, setReset] = useState(initial)
  const [steps, setSteps] = useState(initialSteps)
  const [gaveUp, setGaveUp] = useState(false)
  const id = initial?.reset_id

  useEffect(() => {
    if (!id || isDone(initial)) return
    let alive = true
    let polls = 0
    let timer = null
    const tick = async () => {
      polls++
      try {
        const res = await fetch(`/api/ancalagon/resets?device=${encodeURIComponent(name)}&limit=10`)
        if (res.ok) {
          const body = await res.json()
          const row = (body?.resets || []).find(r => r.reset_id === id)
          if (row && alive) {
            setReset(row)
            if (Array.isArray(row.steps) && row.steps.length) setSteps(row.steps)
            if (isDone(row)) return
          }
        }
      } catch { /* rede: tenta de novo no próximo ciclo */ }
      if (!alive) return
      if (polls >= POLL_MAX) { setGaveUp(true); return }
      timer = setTimeout(tick, POLL_MS)
    }
    timer = setTimeout(tick, POLL_MS)
    return () => { alive = false; clearTimeout(timer) }
    // Um acompanhamento por reset: o `key` do tracker remonta o diálogo num reset novo,
    // então o `initial` não entra nas dependências.
  }, [id, name])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const status = reset?.status || 'requested'
  const done = isDone(reset)
  const warnings = Array.isArray(reset?.warnings) ? reset.warnings : []
  const tone = statusTone(status)
  const spinning = tone.Icon === Loader2 && !gaveUp
  const code = errorCode(reset?.error)

  let note = null
  if (status === 'rejected') {
    note = { cls: 'text-status-down', text: resetErrorText(t, { code, message: reset?.error }) }
  } else if (done && status === 'failed') {
    note = { cls: 'text-status-down', text: KNOWN_ABORTS.has(code) ? t(`ops:devices.reset.aborts.${code}`) : t('ops:devices.reset.local_kept') }
  } else if (done) {
    note = { cls: 'text-status-op', text: t('ops:devices.reset.local_cleaned') }
  } else if (gaveUp) {
    note = { cls: 'text-amber-700 dark:text-amber-300', text: t('ops:devices.reset.polling_stopped') }
  } else if (status === 'unknown') {
    note = { cls: 'text-amber-700 dark:text-amber-300', text: t('ops:devices.reset.errors.ancalagon_unreachable') }
  } else if (FINAL.has(status)) {
    note = { cls: 'text-slate-500 dark:text-slate-400', text: t('ops:devices.reset.waiting_callback') }
  } else {
    note = { cls: 'text-slate-500 dark:text-slate-400', text: t('ops:devices.reset.in_progress') }
  }
  const showRawError = reset?.error && status === 'failed' && !KNOWN_ABORTS.has(code)

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="min-w-0">
            <h2 className="font-semibold text-slate-800 dark:text-slate-100 truncate">{t('ops:devices.reset.progress_title', { name })}</h2>
            <span className={`inline-flex items-center gap-1.5 mt-1.5 text-[11px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${tone.cls}`}>
              <tone.Icon size={12} className={spinning ? 'animate-spin' : ''} />
              {t(`ops:devices.reset.status.${status}`, { defaultValue: status })}
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto scrollbar-thin">
          <p className={`text-sm ${note.cls}`}>{note.text}</p>

          {warnings.length > 0 && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-300 px-3 py-2 space-y-1">
              {warnings.map(w => (
                <p key={w} className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                  <AlertTriangle size={13} className="shrink-0 mt-px" />
                  <span>{t(`ops:devices.reset.warnings.${w}`, { defaultValue: w })}</span>
                </p>
              ))}
            </div>
          )}

          {showRawError && <p className="text-[11px] font-mono text-status-down break-words">{reset.error}</p>}

          <dl className="text-xs divide-y divide-slate-100 dark:divide-slate-800 border-y border-slate-100 dark:border-slate-800">
            <div className="flex justify-between gap-3 py-1.5">
              <dt className="text-slate-400 dark:text-slate-500 shrink-0">{t('ops:devices.reset.reset_id')}</dt>
              <dd className="font-mono text-slate-600 dark:text-slate-300 truncate">{reset?.reset_id}</dd>
            </div>
            {reset?.requested_by && (
              <div className="flex justify-between gap-3 py-1.5">
                <dt className="text-slate-400 dark:text-slate-500 shrink-0">{t('ops:devices.reset.requested_by')}</dt>
                <dd className="text-slate-600 dark:text-slate-300 truncate">
                  {reset.requested_by}{reset.created_at ? ` · ${fmtDateTime(reset.created_at)}` : ''}
                </dd>
              </div>
            )}
            {reset?.reason && (
              <div className="flex justify-between gap-3 py-1.5">
                <dt className="text-slate-400 dark:text-slate-500 shrink-0">{t('ops:devices.reset.reason')}</dt>
                <dd className="text-slate-600 dark:text-slate-300 text-right break-words">{reset.reason}</dd>
              </div>
            )}
            {reset?.ticket && (
              <div className="flex justify-between gap-3 py-1.5">
                <dt className="text-slate-400 dark:text-slate-500 shrink-0">{t('ops:devices.reset.ticket_label')}</dt>
                <dd className="font-mono text-slate-600 dark:text-slate-300 truncate">{reset.ticket}</dd>
              </div>
            )}
          </dl>

          {steps.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">{t('ops:devices.reset.steps_title')}</p>
              <ul className="space-y-1 text-xs">
                {steps.map((s, i) => (
                  <li key={`${s.name}-${i}`} className="flex items-start gap-1.5">
                    <span className={`mt-[5px] w-1.5 h-1.5 rounded-full shrink-0 ${stepDot(s.status)}`} />
                    <div className="min-w-0">
                      <span className="text-slate-700 dark:text-slate-200">
                        {t(`ops:devices.reset.steps.${String(s.name || '').replace(/\./g, '_')}`, { defaultValue: s.name })}
                      </span>
                      <span className="text-slate-400 dark:text-slate-500"> · {t(`ops:devices.reset.step_status.${s.status}`, { defaultValue: s.status })}</span>
                      {s.detail && <p className="text-[11px] text-slate-400 dark:text-slate-500 break-words">{s.detail}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-end px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="text-sm font-semibold px-3 py-1.5 rounded-md text-white bg-slate-800 hover:bg-slate-700"
          >
            {t('common:actions.close')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
